import { GraphQLResult } from '@aws-amplify/api-graphql';
import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';

import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';

// --- GraphQL Operations ---
//
// Inlined (not imported from the auto-generated src/graphql/{queries,mutations}.ts)
// so this CDK-facing service doesn't couple to the Amplify schema's lifecycle —
// those documents are slated for removal once the Amplify Procore resolver is
// decommissioned.

const getOrRefreshProcoreAccessTokenMutation = /* GraphQL */ `
  mutation GetOrRefreshProcoreAccessToken($input: GetOrRefreshProcoreAccessTokenInput!) {
    getOrRefreshProcoreAccessToken(input: $input) {
      access_token
      token_type
      expires_in
      refresh_token
      created_at
    }
  }
`;

const getProcoreCompaniesQuery = /* GraphQL */ `
  query GetProcoreCompanies($input: GetProcoreCompaniesInput!) {
    getProcoreCompanies(input: $input) {
      id
      name
      is_active
      logo_url
    }
  }
`;

const getProcoreProjectsQuery = /* GraphQL */ `
  query GetProcoreProjects($input: GetProcoreProjectsInput!) {
    getProcoreProjects(input: $input) {
      id
      active
      display_name
      name
      created_at
      start_date
      completion_date
      project_number
      project_stage {
        id
        name
      }
      project_template {
        id
        name
      }
      total_value
    }
  }
`;

const getProcoreProjectUsersQuery = /* GraphQL */ `
  query GetProcoreProjectUsers($input: GetProcoreProjectUsersInput!) {
    getProcoreProjectUsers(input: $input) {
      id
      name
      email_address
    }
  }
`;

const getProcoreWBSCodesQuery = /* GraphQL */ `
  query GetProcoreWBSCodes($input: GetProcoreWBSCodesInput!) {
    getProcoreWBSCodes(input: $input) {
      id
      flat_code
      flat_name
      description
      status
      segment_items {
        id
        code
        name
        status
      }
    }
  }
`;

const createOrUpdateProcoreDirectCostMutation = /* GraphQL */ `
  mutation CreateOrUpdateProcoreDirectCost($input: CreateOrUpdateProcoreDirectCostInput!) {
    createOrUpdateProcoreDirectCost(input: $input) {
      transactionId
      directCostId
      directCostLineItemId
    }
  }
`;

const createReimbursementDirectCostMutation = /* GraphQL */ `
  mutation CreateReimbursementDirectCost($input: CreateReimbursementDirectCostInput!) {
    createReimbursementDirectCost(input: $input) {
      reimbursementTransactionId
      directCostId
      directCostLineItemId
    }
  }
`;

const attachDirectCostReceiptMutation = /* GraphQL */ `
  mutation AttachDirectCostReceipt($input: AttachDirectCostReceiptInput!) {
    attachDirectCostReceipt(input: $input) {
      directCostId
    }
  }
`;

// --- Input Types ---

export interface GetOrRefreshProcoreAccessTokenInputData {
  code?: string | null;
  refresh_token?: string | null;
  redirect_uri?: string | null;
}

export interface GetProcoreCompaniesInputData {
  auth: string;
}

export interface GetProcoreProjectsInputData {
  auth: string;
  companyId: number;
}

export interface GetProcoreProjectUsersInputData {
  auth: string;
  search: string;
  projectId: number;
  companyId: number;
}

export interface GetProcoreWBSCodesInputData {
  auth: string;
  projectId: number;
  companyId: number;
}

export interface CreateOrUpdateProcoreDirectCostInputData {
  auth: string;
  transactionId: string;
  companyId: number;
}

export interface CreateReimbursementDirectCostInputData {
  auth: string;
  companyId: number;
  reimbursementTransactionId: string;
}

export interface AttachDirectCostReceiptInputData {
  auth: string;
  projectId: number;
  directCostId: number;
  companyId: number;
  receiptPath: string;
}

// --- Response Types ---

export interface GetOrRefreshProcoreAccessTokenPayload {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  created_at: number;
}

export interface GetProcoreCompaniesPayload {
  id: number;
  name: string;
  is_active: boolean;
  logo_url?: string | null;
}

export interface ProcoreProjectStage {
  id: number;
  name: string;
}

export interface ProcoreProjectTemplate {
  id: number;
  name: string;
}

export interface GetProcoreProjectsPayload {
  id: number;
  active: boolean;
  display_name: string;
  name: string;
  created_at: string;
  start_date?: string | null;
  completion_date?: string | null;
  project_number?: string | null;
  project_stage?: ProcoreProjectStage | null;
  project_template?: ProcoreProjectTemplate | null;
  total_value?: string | null;
}

export interface GetProcoreProjectUsersPayload {
  id: number;
  name: string;
  email_address?: string | null;
}

export interface ProcoreSegmentItem {
  id: number;
  code?: string | null;
  name?: string | null;
  status?: string | null;
}

export interface GetProcoreWBSCodesPayload {
  id: number;
  flat_code?: string | null;
  flat_name?: string | null;
  description?: string | null;
  status?: string | null;
  segment_items?: ProcoreSegmentItem[] | null;
}

export interface CreateOrUpdateProcoreDirectCostPayload {
  transactionId: string;
  directCostId: number;
  directCostLineItemId: number;
}

export interface CreateReimbursementDirectCostPayload {
  reimbursementTransactionId: string;
  directCostId: number;
  directCostLineItemId: number;
}

export interface AttachDirectCostReceiptPayload {
  directCostId: number;
}

// --- Service ---

/**
 * Service for Procore operations via the CDK AppSync API.
 *
 * Follows the same pattern as PaidolService: uses fetch() + Cognito JWT to
 * call the CDK-managed AppSync endpoint (REACT_APP_CDK_APPSYNC_ENDPOINT). The
 * `operationName` inside each `input` payload is passed through untouched —
 * the CDK Lambda dispatches on it, and it differs from the GraphQL field name.
 */
export class ProcoreService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  async getOrRefreshProcoreAccessToken(
    data: GetOrRefreshProcoreAccessTokenInputData
  ): Promise<GetOrRefreshProcoreAccessTokenPayload | undefined> {
    const input = { operationName: 'getOrRefreshAccessToken', data };
    const result = await this.execute<{
      getOrRefreshProcoreAccessToken?: GetOrRefreshProcoreAccessTokenPayload;
    }>(getOrRefreshProcoreAccessTokenMutation, { input }, 'getOrRefreshProcoreAccessToken');
    return result.getOrRefreshProcoreAccessToken ?? undefined;
  }

  async getProcoreCompanies(
    data: GetProcoreCompaniesInputData
  ): Promise<GetProcoreCompaniesPayload[] | undefined> {
    const input = { operationName: 'getCompanies', data };
    const result = await this.execute<{
      getProcoreCompanies?: GetProcoreCompaniesPayload[];
    }>(getProcoreCompaniesQuery, { input }, 'getProcoreCompanies');
    return result.getProcoreCompanies ?? undefined;
  }

  async getProcoreProjects(
    data: GetProcoreProjectsInputData
  ): Promise<GetProcoreProjectsPayload[] | undefined> {
    const input = { operationName: 'getProjects', data };
    const result = await this.execute<{
      getProcoreProjects?: GetProcoreProjectsPayload[];
    }>(getProcoreProjectsQuery, { input }, 'getProcoreProjects');
    return result.getProcoreProjects ?? undefined;
  }

  async getProcoreProjectUsers(
    data: GetProcoreProjectUsersInputData
  ): Promise<GetProcoreProjectUsersPayload[] | undefined> {
    const input = { operationName: 'getProjectUsers', data };
    const result = await this.execute<{
      getProcoreProjectUsers?: GetProcoreProjectUsersPayload[];
    }>(getProcoreProjectUsersQuery, { input }, 'getProcoreProjectUsers');
    return result.getProcoreProjectUsers ?? undefined;
  }

  async getProcoreWBSCodes(
    data: GetProcoreWBSCodesInputData
  ): Promise<GetProcoreWBSCodesPayload[] | undefined> {
    const input = { operationName: 'getWBSCodes', data };
    const result = await this.execute<{
      getProcoreWBSCodes?: GetProcoreWBSCodesPayload[];
    }>(getProcoreWBSCodesQuery, { input }, 'getProcoreWBSCodes');
    return result.getProcoreWBSCodes ?? undefined;
  }

  async createOrUpdateProcoreDirectCost(
    data: CreateOrUpdateProcoreDirectCostInputData
  ): Promise<CreateOrUpdateProcoreDirectCostPayload | undefined> {
    const input = { operationName: 'createOrUpdateDirectCost', data };
    const result = await this.execute<{
      createOrUpdateProcoreDirectCost?: CreateOrUpdateProcoreDirectCostPayload;
    }>(createOrUpdateProcoreDirectCostMutation, { input }, 'createOrUpdateProcoreDirectCost');
    return result.createOrUpdateProcoreDirectCost ?? undefined;
  }

  async createReimbursementDirectCost(
    data: CreateReimbursementDirectCostInputData
  ): Promise<CreateReimbursementDirectCostPayload | undefined> {
    const input = { operationName: 'createReimbursementDirectCost', data };
    const result = await this.execute<{
      createReimbursementDirectCost?: CreateReimbursementDirectCostPayload;
    }>(createReimbursementDirectCostMutation, { input }, 'createReimbursementDirectCost');
    return result.createReimbursementDirectCost ?? undefined;
  }

  async attachDirectCostReceipt(
    data: AttachDirectCostReceiptInputData
  ): Promise<AttachDirectCostReceiptPayload | undefined> {
    const input = { operationName: 'attachDirectCostReceipt', data };
    const result = await this.execute<{
      attachDirectCostReceipt?: AttachDirectCostReceiptPayload;
    }>(attachDirectCostReceiptMutation, { input }, 'attachDirectCostReceipt');
    return result.attachDirectCostReceipt ?? undefined;
  }

  // --- Private helpers ---

  private async execute<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'procore', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const body = JSON.stringify({ query, variables });
      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();

      const response = await fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: jwt,
        },
        body,
      });

      const json = (await response.json()) as GraphQLResult<T> & {
        errors?: Array<{ message: string }>;
      };

      reportCdkRefusal(json?.errors, { authMode: 'cognito', operationName });

      if (!response.ok) {
        const message = json?.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors.map((e) => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        captureException(error, {
          tags: { area: 'procore', type: isNetworkTransportError(error) ? 'network-transport' : 'graphql' },
          extra: { operation: operationName, endpoint: this.cdkAppSyncEndpoint },
        });
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }
}

// Export a singleton instance for convenience
export const procoreService = new ProcoreService();
